import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Divider,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InherentRiskPanel from "../components/InherentRiskPanel";
import VendorRiskBadge from "../../components/VendorRiskBadge";
import StatusChip from "../../components/StatusChip";
import { SECURITY_REVIEW_STATUS, getDisplayedReviewStatus, formatDecisionLabel } from "../../utils/constants";
import { styles } from "../../styles/Vendors.styles";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "-";

const SummaryRow = ({ label, children }) => {
  const theme = useTheme();
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.25 }}>
        <Typography variant="body2">{label}</Typography>
        <Box>{children}</Box>
      </Box>
      <Divider sx={{ borderColor: theme.customColors.border.main }} />
    </>
  );
};

const OverviewTab = ({ vendor, onOpenReview }) => {
  const theme = useTheme();
  const reviews = vendor.reviews || [];
  const latestReview = [...reviews].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  )[0];
  const latestCompleted = [...reviews]
    .filter((r) => r.status === SECURITY_REVIEW_STATUS.COMPLETED)
    .sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt))[0];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <InherentRiskPanel vendor={vendor} />
      </Grid>
      <Grid item xs={12} md={5}>
        <Card sx={{ ...styles.mainCard, height: "100%" }} variant="outlined">
          <CardContent>
            <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong} sx={{ mb: 1 }}>
              Risk Summary
            </Typography>

            <SummaryRow label="Inherent risk">
              <VendorRiskBadge level={vendor.inherentRisk} />
            </SummaryRow>
            <SummaryRow label="Residual risk">
              <VendorRiskBadge level={vendor.residualRisk} />
            </SummaryRow>
            <SummaryRow label="Review status">
              <StatusChip label={getDisplayedReviewStatus(vendor)} />
            </SummaryRow>
            <SummaryRow label="Latest review">
              <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
                {latestReview?.status || "Not started"}
              </Typography>
            </SummaryRow>
            <SummaryRow label="Recommendation">
              {latestCompleted?.recommendation ? (
                <StatusChip label={latestCompleted.recommendation} />
              ) : vendor.decision ? (
                <StatusChip label={formatDecisionLabel(vendor.decision)} />
              ) : (
                <Typography variant="body2">Pending</Typography>
              )}
            </SummaryRow>
            <SummaryRow label="Last reviewed">
              <Typography variant="body2">{formatDate(vendor.lastReviewedAt)}</Typography>
            </SummaryRow>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 1.25 }}>
              <Typography variant="body2">Next review</Typography>
              <Typography variant="body2">{formatDate(vendor.nextReviewDate)}</Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 16 }} />}
              onClick={onOpenReview}
              sx={{ ...styles.addButton, mt: 2.5 }}
            >
              Go to Security Review
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OverviewTab;
